import type { NavigationSource } from '@hibro/platform-renderer';
import { surfaceInstanceId } from '@hibro/shared';

/**
 * App-shell navigation registry (架构方案 §5.8 跳转, 见 8). Bridges the preload
 * `window.hibroNavigation.onViewNavigate` fan-out (the main process forwards every
 * `navigate(view:<id>[::<key>], …)` to all windows) into the platform's
 * {@link NavigationSource}. Buffers the latest location per **view instance** so a
 * navigation that arrives before the view mounts is delivered on mount, and
 * fans subsequent ones to the live subscriber. Kept free of the platform package
 * so platform-renderer stays free of the preload bridge.
 *
 * Buffering and routing are keyed by instance id (§5.7): with a bare view-id key
 * two open documents would overwrite each other's buffered location and each
 * receive the other's navigations.
 */
class NavigationRegistry implements NavigationSource {
  /** Latest location per instance id; latest-wins (navigation positions to one place). */
  private readonly latest = new Map<string, unknown>();
  /**
   * A navigation that arrived with nobody listening, kept until its present(面)
   * happens. **One-shot**, unlike the `latest` locations above: opening/selecting
   * a tab is a single action, while a location stays readable through
   * {@link current} for whichever view mounts later.
   *
   * Re-subscribing therefore must not replay it. The workbench re-installs its
   * present hook whenever that hook's inputs change — the detached-surface set,
   * the contribution list — and a replay there would yank the user off the tab
   * they are on: detaching (or attaching back) any view would jump the editor to
   * whatever was navigated to last.
   */
  private pendingPresentation?: readonly [
    viewId: string,
    location: unknown,
    instanceKey: string | undefined,
  ];
  private readonly listeners = new Map<
    string,
    Set<(location: unknown) => void>
  >();
  /** Listeners on every navigation (the workbench's present(面) hook, 见 8). */
  private readonly allListeners = new Set<
    (viewId: string, location: unknown, instanceKey: string | undefined) => void
  >();

  constructor() {
    window.hibroNavigation?.onViewNavigate((viewId, location, instanceKey) =>
      this.deliver(viewId, location, instanceKey),
    );
  }

  /**
   * Not part of {@link NavigationSource} — that interface is what *consumers*
   * see. The producers are the runtime entries: Electron's preload fan-out in
   * the constructor above, and the browser's host relay via
   * {@link deliverViewNavigation}.
   */
  deliver(viewId: string, location: unknown, instanceKey?: string): void {
    const instanceId = surfaceInstanceId(viewId, instanceKey);
    this.latest.set(instanceId, location);
    // present(面) listeners first so the workbench opens/selects the target view
    // instance; the per-instance listener (or useViewNavigation on mount via
    // current()) then delivers the location (点) to it.
    const presenters = [...this.allListeners];
    this.pendingPresentation =
      presenters.length === 0 ? [viewId, location, instanceKey] : undefined;
    for (const listener of presenters) {
      listener(viewId, location, instanceKey);
    }
    const set = this.listeners.get(instanceId);
    if (set) {
      for (const listener of [...set]) {
        listener(location);
      }
    }
  }

  subscribe(
    viewId: string,
    instanceKey: string | undefined,
    listener: (location: unknown) => void,
  ): () => void {
    const instanceId = surfaceInstanceId(viewId, instanceKey);
    let set = this.listeners.get(instanceId);
    if (!set) {
      set = new Set();
      this.listeners.set(instanceId, set);
    }
    set.add(listener);
    return () => {
      const current = this.listeners.get(instanceId);
      if (!current) {
        return;
      }
      current.delete(listener);
      if (current.size === 0) {
        this.listeners.delete(instanceId);
      }
    };
  }

  current(viewId: string, instanceKey?: string): unknown {
    return this.latest.get(surfaceInstanceId(viewId, instanceKey));
  }

  subscribeAll(
    listener: (
      viewId: string,
      location: unknown,
      instanceKey: string | undefined,
    ) => void,
  ): () => void {
    this.allListeners.add(listener);
    // Present any navigation that arrived before the workbench installed its
    // area-routing listener. Per-instance subscribers already recover the
    // location through current(); the present(面) listener must likewise recover
    // which editor view instance should be opened — once.
    const pending = this.pendingPresentation;
    if (pending) {
      this.pendingPresentation = undefined;
      listener(...pending);
    }
    return () => this.allListeners.delete(listener);
  }
}

let singleton: NavigationRegistry | undefined;

function registry(): NavigationRegistry {
  singleton ??= new NavigationRegistry();
  return singleton;
}

/**
 * The per-window navigation registry singleton (见 8). One instance buffers
 * locations across view mounts; shared by the workbench and any detached window
 * content rendered in this renderer.
 */
export function getNavigationRegistry(): NavigationSource {
  return registry();
}

/**
 * Deliver a `view:<viewId>[::<instanceKey>]` navigation that arrived from outside
 * the preload bridge — the browser runtime's host relay (见 18), where there is no
 * `window.hibroNavigation` to subscribe to.
 *
 * A plain function rather than an injected source on {@link getNavigationRegistry}
 * because the registry is a lazy singleton with several call sites: passing the
 * source as an argument would make behaviour depend on which one runs first.
 * Arriving before the workbench mounts is fine and needs no ordering guarantee —
 * buffering the latest location per instance is what this class is for.
 */
export function deliverViewNavigation(
  viewId: string,
  location: unknown,
  instanceKey?: string,
): void {
  registry().deliver(viewId, location, instanceKey);
}
